import { useContext, useMemo } from "react";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DictContext } from "@/layout/context/DictContext";
import SText from "@/styles/SText";
import { Equipment, EquipmentEnumItem } from "@/types/equipment";

// 我的领用列 Hook 所需的设备状态枚举。
interface UseMyBorrowedColumnProps {
  statusItems: EquipmentEnumItem[];
}

// 普通字段统一以短横线表示空值。
const renderText = (value?: string | number | null) => (
  <SText
    text={value === null || value === undefined || value === ""
      ? "-"
      : String(value)}
    style={{ width: "100%" }}
  />
);

// 生成我的领用列表列，统一格式化购置、状态与保养信息。
const useMyBorrowedColumn = ({
  statusItems,
}: UseMyBorrowedColumnProps): ColumnsType<Equipment> => {
  const { t } = useTranslation();
  const { currencys } = useContext(DictContext);

  return useMemo<ColumnsType<Equipment>>(() => [
    {
      title: t("equipment.equipmentNumber"),
      dataIndex: "number",
      width: 150,
      fixed: "left",
      render: renderText,
    },
    {
      title: t("equipment.equipmentName"),
      dataIndex: "name",
      width: 180,
      fixed: "left",
      render: renderText,
    },
    {
      title: t("equipment.brand"),
      dataIndex: "brand",
      width: 130,
      render: renderText,
    },
    {
      title: t("equipment.model"),
      dataIndex: "model",
      width: 140,
      render: renderText,
    },
    {
      title: t("equipment.serialNo"),
      dataIndex: "serialNo",
      width: 160,
      render: renderText,
    },
    // 购置信息使用分组表头，币种按字典文案显示。
    {
      title: t("equipment.purchaseInfo"),
      key: "purchaseInfo",
      children: [
        {
          title: t("equipment.purchasePrice"),
          dataIndex: "purchasePrice",
          width: 130,
          render: renderText,
        },
        {
          title: t("equipment.purchaseCurrency"),
          dataIndex: "purchaseCurrency",
          width: 130,
          render: (value: number | null) => renderText(
            value === null
              ? null
              : currencys.find(({ key }) => String(key) === String(value))
                ?.desc ?? value,
          ),
        },
        {
          title: t("equipment.purchaseAt"),
          dataIndex: "purchaseAt",
          width: 120,
          render: (value: number | null) => renderText(
            value === null ? null : dayjs(value).format("YYYY-MM-DD"),
          ),
        },
      ],
    },
    // 状态文案来自接口枚举，未知值回退为原始数值。
    {
      title: t("equipment.equipmentStatus"),
      dataIndex: "status",
      width: 120,
      render: (status: number) => (
        <SText
          text={statusItems.find(({ v }) => v === status)?.desc ??
            String(status)}
          tagMode
          style={{ textAlign: "left" }}
          tagStyle={{ marginInlineEnd: 0 }}
        />
      ),
    },
    {
      title: t("equipment.location"),
      dataIndex: "location",
      width: 150,
      render: renderText,
    },
    {
      title: t("equipment.placeLocation"),
      dataIndex: "placeLocation",
      width: 150,
      render: renderText,
    },
    {
      title: t("equipment.supplier"),
      dataIndex: "supplier",
      width: 150,
      render: renderText,
    },
    {
      title: t("equipment.maintainDays"),
      dataIndex: "maintainDays",
      width: 150,
      render: renderText,
    },
    {
      title: t("equipment.maintainThreshold"),
      dataIndex: "maintainThreshold",
      width: 180,
      render: renderText,
    },
    {
      title: t("equipment.currentOdometer"),
      dataIndex: "currentOdometer",
      width: 120,
      render: renderText,
    },
    {
      title: t("equipment.lastMaintainOdometer"),
      dataIndex: "lastMaintainOdometer",
      width: 140,
      render: renderText,
    },
    {
      title: t("equipment.lastMaintainAt"),
      dataIndex: "lastMaintainAt",
      width: 170,
      render: (value: number | null) => renderText(
        value === null ? null : dayjs(value).format("YYYY-MM-DD HH:mm"),
      ),
    },
  ], [currencys, statusItems, t]);
};

export default useMyBorrowedColumn;
